import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import adminApi from "../services/api";
import RichTextEditor from "../components/RichTextEditor";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:5001";

const CATEGORIES = [
	"Portrait",
	"Landscape",
	"Cinematic",
	"Black & White",
	"Vintage",
	"Teal & Orange",
	"Travel",
];

const validateDriveLink = (url) => {
	if (!url) return true;
	// Accept both file and folder links
	const driveRegex = /^https:\/\/drive\.google\.com\/(file\/d\/[\w-]+(\/.*)?|open\?id=[\w-]+|drive\/folders\/[\w-]+)(.*)$/;
	return driveRegex.test(url.trim());
};

const normalizeUrl = (value) => {
	if (!value) return "";
	const trimmed = value.trim();
	if (!/^https?:\/\//i.test(trimmed)) {
		return `https://${trimmed}`;
	}
	return trimmed;
};

const toCurrency = (value) => {
	const num = typeof value === "number" ? value : parseFloat(String(value).replace(/[^0-9.]/g, ""));
	return isNaN(num) ? "Rs 0.00" : `Rs ${num.toFixed(2)}`;
};

// Safely resolve an image value (string | object) to an absolute URL
const resolveImage = (img, apiBase) => {
	if (!img) return '';
	const raw = typeof img === 'string' ? img : (img.path || img.url || '');
	if (!raw) return '';
	// If already an absolute URL, return as-is
	if (/^https?:\/\//i.test(raw)) return raw;
	// Ensure proper formatting: apiBase should not have trailing slash, path should start with /
	const cleanBase = apiBase ? apiBase.replace(/\/$/, '') : '';
	const cleanPath = raw.startsWith('/') ? raw : `/${raw}`;
	return `${cleanBase}${cleanPath}`;
};

const Presets = () => {
	// Data
	const [presets, setPresets] = useState([]);
	const [editingPresetId, setEditingPresetId] = useState(null);

	// UI State
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isLoading, setIsLoading] = useState({ presets: false, form: false, driveTest: false });
	const [uploadProgress, setUploadProgress] = useState(0);
	const [search, setSearch] = useState("");
	const [categoryFilter, setCategoryFilter] = useState("");

	// Form State
	const [formData, setFormData] = useState({
		name: "",
		description: "",
		descriptionType: "text",
		descriptionItems: [],
		category: "",
		price: "",
		originalPrice: "",
		driveLink: "",
		images: [],
		imagePreviews: [],
	});
	const fileInputRef = useRef(null);

	// Track existing images and deletions when editing
	const [existingPreviewCount, setExistingPreviewCount] = useState(0);
	const [deletedExistingIndices, setDeletedExistingIndices] = useState([]);
	const [existingIndexMap, setExistingIndexMap] = useState([]); // maps preview index -> original image index

	// Fetch presets
	useEffect(() => {
		fetchPresets();
	}, []);

	const fetchPresets = async () => {
		setIsLoading((p) => ({ ...p, presets: true }));
		try {
			const response = await adminApi.presets.getAll();

			let presetsData = [];
			if (Array.isArray(response)) presetsData = response;
			else if (response?.data?.presets) presetsData = response.data.presets;
			else if (Array.isArray(response?.data)) presetsData = response.data;

			if (!Array.isArray(presetsData)) presetsData = [];

			setPresets(
				presetsData.map((p) => ({
					...p,
					price: p.price,
					_images: Array.isArray(p.images)
						? p.images.map((img) => (typeof img === 'string' ? img : (img?.url || img?.path || '')))
						: [],
				}))
			);
		} catch (error) {
			console.error("Failed to load presets:", error);
			const msg = error.response?.data?.message || error.message || "Failed to fetch presets";
			toast.error(msg);
		} finally {
			setIsLoading((p) => ({ ...p, presets: false }));
		}
	};

	// Derived list
	const filteredPresets = useMemo(() => {
		const term = search.trim().toLowerCase();
		return presets.filter((p) => {
			const matchesTerm = !term || p.name?.toLowerCase().includes(term) || p.description?.toLowerCase().includes(term);
			const matchesCat = !categoryFilter || p.category === categoryFilter;
			return matchesTerm && matchesCat;
		});
	}, [presets, search, categoryFilter]);

	// Form helpers
	const resetForm = () => {
		setEditingPresetId(null);
		setFormData({ name: "", description: "", descriptionType: "text", descriptionItems: [], category: "", price: "", originalPrice: "", driveLink: "", images: [], imagePreviews: [] });
		setExistingPreviewCount(0);
		setDeletedExistingIndices([]);
		setExistingIndexMap([]);
		setUploadProgress(0);
		fileInputRef.current && (fileInputRef.current.value = "");
	};

	const openCreate = () => {
		resetForm();
		setIsModalOpen(true);
	};

	const openEdit = (preset) => {
		// Set editing ID first before any other state changes
		setEditingPresetId(preset.id);

		// Build image previews from the backend images
		const previews = (preset._images || []).map((img) => {
			if (typeof img === "string") {
				return img.startsWith("http") ? img : `${API_URL}${img}`;
			}
			return img;
		});

		setFormData({
			name: preset.name || "",
			description: preset.description || "",
			descriptionType: preset.descriptionType || "text",
			descriptionItems: Array.isArray(preset.descriptionItems) ? preset.descriptionItems : [],
			category: preset.category || "",
			price: String(preset.price ?? ""),
			originalPrice: preset.originalPrice ? String(preset.originalPrice) : "",
			driveLink: preset.driveLink || "",
			images: [],
			imagePreviews: previews,
		});
		setExistingPreviewCount(previews.length);
		setExistingIndexMap(previews.map((_, i) => i));
		setDeletedExistingIndices([]);
		setIsModalOpen(true);
	};

	// Client-side image compression function
	const compressImage = (file, maxWidth = 1920, quality = 0.8) => {
		return new Promise((resolve) => {
			const canvas = document.createElement('canvas');
			const ctx = canvas.getContext('2d');
			const img = new Image();

			img.onload = () => {
				// Calculate new dimensions
				let { width, height } = img;
				if (width > maxWidth) {
					height = (height * maxWidth) / width;
					width = maxWidth;
				}

				// Set canvas dimensions
				canvas.width = width;
				canvas.height = height;

				// Draw and compress
				ctx.drawImage(img, 0, 0, width, height);
				canvas.toBlob(resolve, 'image/jpeg', quality);
			};

			img.src = URL.createObjectURL(file);
		});
	};

	const handleImageChange = async (e) => {
		const files = Array.from(e.target.files || []);
		if (!files.length) return;
		// Use previews length as the single source of truth for how many are already selected/displayed
		const current = formData.imagePreviews.length;
		const remaining = Math.max(0, 30 - current);
		const take = files.slice(0, remaining);

		const valid = [];
		const previews = [];

		// Show loading toast for compression
		if (take.length > 1) {
			toast.loading(`Compressing ${take.length} images...`, { id: 'compressing' });
		}

		for (const f of take) {
			if (!f.type.startsWith("image/")) {
				toast.error(`${f.name} is not an image`);
				continue;
			}
			if (f.size > 15 * 1024 * 1024) {
				toast.error(`${f.name} exceeds 15MB`);
				continue;
			}

			try {
				// Compress image if it's larger than 2MB
				let processedFile = f;
				if (f.size > 2 * 1024 * 1024) {
					processedFile = await compressImage(f, 1920, 0.85);
					processedFile.name = f.name;
					console.log(`Compressed ${f.name}: ${(f.size / 1024 / 1024).toFixed(2)}MB → ${(processedFile.size / 1024 / 1024).toFixed(2)}MB`);
				}

				valid.push(processedFile);
				previews.push(URL.createObjectURL(processedFile));
			} catch (error) {
				console.error(`Error compressing ${f.name}:`, error);
				// Use original file if compression fails
				valid.push(f);
				previews.push(URL.createObjectURL(f));
			}
		}

		// Dismiss loading toast
		toast.dismiss('compressing');

		if (!valid.length) return;
		setFormData((prev) => ({
			...prev,
			images: [...prev.images, ...valid],
			imagePreviews: [...prev.imagePreviews, ...previews],
		}));
		toast.success(`${valid.length} image(s) added`);
	};

	const removeImage = (index) => {
		setFormData((prev) => {
			const previews = [...prev.imagePreviews];
			const images = [...prev.images];
			if (index < previews.length) {
				const url = previews[index];
				try { URL.revokeObjectURL(url); } catch { }
				previews.splice(index, 1);
				// If removing an existing preview when editing, map to original index using existingIndexMap
				if (editingPresetId && index < existingIndexMap.length) {
					const originalIndex = existingIndexMap[index];
					setDeletedExistingIndices((prevDel) => Array.from(new Set([...prevDel, originalIndex])));
					// Remove the mapping entry for this preview
					setExistingIndexMap((map) => {
						const next = [...map];
						next.splice(index, 1);
						return next;
					});
					setExistingPreviewCount((c) => Math.max(0, c - 1));
				} else {
					// Removing a newly added image: adjust images array offset via remaining existingIndexMap length
					const offset = existingIndexMap.length; // number of existing previews left
					const newIndex = index - offset;
					if (newIndex >= 0 && images[newIndex]) images.splice(newIndex, 1);
				}
			}
			return { ...prev, imagePreviews: previews, images };
		});
	};

	const testDriveLink = async () => {
		const url = normalizeUrl(formData.driveLink);
		if (!url) return toast.error("Please enter a Google Drive link");
		if (!validateDriveLink(url)) return toast.error("Enter a valid Google Drive file link");
		setIsLoading((p) => ({ ...p, driveTest: true }));
		const loading = toast.loading("Testing Drive link...");
		try {
			await adminApi.presets.testDriveLink(url);
			toast.success("Drive link is valid and accessible!");
		} catch (error) {
			const status = error.response?.status;
			if (status === 403) toast.error("File not public. Set sharing to 'Anyone with the link'.");
			else if (status === 404) toast.error("File not found. Check the URL.");
			else toast.error(error.response?.data?.message || "Failed to verify Drive link");
		} finally {
			toast.dismiss(loading);
			setIsLoading((p) => ({ ...p, driveTest: false }));
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		// Basic validation
		if (!formData.name.trim() || !formData.category || formData.price === "") {
			return toast.error("Please fill in all required fields");
		}
		// Drive link is required
		if (!formData.driveLink) {
			return toast.error("Google Drive link is required");
		}
		if (!validateDriveLink(normalizeUrl(formData.driveLink))) {
			return toast.error("Enter a valid Google Drive file or folder link");
		}

		setIsLoading((p) => ({ ...p, form: true }));
		setUploadProgress(0);

		// Debug: Log current form data before submission
		console.log('🔍 Current form data before submission:', formData);

		try {
			const fd = new FormData();
			fd.append("name", formData.name.trim());
			fd.append("description", formData.description || "");
			fd.append("descriptionType", formData.descriptionType);
			fd.append("descriptionItems", JSON.stringify(formData.descriptionItems));
			fd.append("category", formData.category);
			fd.append("price", String(formData.price));
			if (formData.originalPrice) {
				fd.append("originalPrice", String(formData.originalPrice));
			}
			fd.append("driveLink", normalizeUrl(formData.driveLink));

			// Debug logging — include image count so we can confirm new images are sent
			const imageCount = (formData.images && formData.images.length) || 0;
			console.log("🔍 Form data being sent:", {
				name: formData.name.trim(),
				description: formData.description,
				descriptionType: formData.descriptionType,
				descriptionItems: formData.descriptionItems,
				category: formData.category,
				price: formData.price,
				driveLink: formData.driveLink,
				editingPresetId,
				imagesCount: imageCount
			});
			if (editingPresetId && imageCount === 0) {
				console.warn("⚠️ No new images in formData.images — did you add images via “Choose files” or drag-and-drop? Existing previews are not re-uploaded.");
			}
			// Append each image; convert Blob to File so multer receives a proper file part
			formData.images.forEach((img, i) => {
				const fileName = (img && (img.name || (img instanceof File && img.name))) || `image-${i + 1}.jpg`;
				const fileToSend = img instanceof File ? img : new File([img], fileName, { type: img?.type || "image/jpeg" });
				fd.append("images", fileToSend, fileName);
			});
			// Send deleted existing indices when editing
			if (editingPresetId && deletedExistingIndices.length) {
				// Ensure unique and sorted (descending handled on server)
				const unique = Array.from(new Set(deletedExistingIndices));
				fd.append("deletedImages", JSON.stringify(unique));
			}

			// Progress callback for upload
			const onProgress = (progress) => {
				setUploadProgress(progress);
			};

			let response;
			if (editingPresetId) {
				console.log('🔍 Updating preset with ID:', editingPresetId);
				response = await adminApi.presets.update(editingPresetId, fd, onProgress);
			} else {
				console.log('🔍 Creating new preset');
				response = await adminApi.presets.create(fd, onProgress);
			}

			console.log('🔍 API Response:', response);

			if (response?.data) {
				console.log('✅ Update successful, response data:', response.data);
				toast.success(editingPresetId ? "Preset updated!" : "Preset created!");
				setIsModalOpen(false);
				resetForm();
				// Merge returned preset (with full image URLs) into list so UI updates immediately
				if (editingPresetId && response.data.preset) {
					const p = response.data.preset;
					const _images = Array.isArray(p.images)
						? p.images.map((img) => (typeof img === "string" ? img : (img?.url || img?.path || "")))
						: [];
					setPresets((prev) =>
						prev.map((preset) =>
							preset.id === p.id
								? { ...preset, ...p, _images }
								: preset
						)
					);
				}
				await fetchPresets();
			} else {
				console.error('❌ No response data received');
				toast.error("No response from server");
			}
		} catch (error) {
			console.error("Save preset failed:", error);
			toast.error(error.response?.data?.message || "Failed to save preset");
		} finally {
			setIsLoading((p) => ({ ...p, form: false }));
		}
	};

	const handleDelete = async (id) => {
		if (!confirm("Delete this preset?")) return;
		try {
			const response = await adminApi.presets.delete(id);
			if (response?.data?.success) {
				setPresets((prev) => prev.filter((p) => p.id !== id));
				toast.success("Preset deleted");
			} else {
				throw new Error(response?.data?.message || "Delete failed");
			}
		} catch (error) {
			console.error("Delete failed:", error);
			toast.error(error.response?.data?.message || "Failed to delete preset");
		}
	};

	return (
		<div className="min-h-screen bg-gray-900 text-white p-4 md:p-6">
			{/* Header */}
			<div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between mb-6">
				<h1 className="text-2xl font-bold">Presets Management</h1>
				<div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
					<div className="flex-1 min-w-[220px]">
						<input
							type="text"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder="Search presets..."
							className="w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
						/>
					</div>
					<select
						value={categoryFilter}
						onChange={(e) => setCategoryFilter(e.target.value)}
						className="px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
					>
						<option value="">All Categories</option>
						{CATEGORIES.map((c) => (
							<option key={c} value={c}>{c}</option>
						))}
					</select>
					<button onClick={openCreate} className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 rounded-lg font-medium">
						+ Add Preset
					</button>
				</div>
			</div>

			{/* Loading */}
			{isLoading.presets ? (
				<div className="flex justify-center items-center h-64">
					<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
				</div>
			) : (
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
					{filteredPresets.map((preset) => (
						<div key={preset.id} className="bg-gray-800 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition">
							{/* Image */}
							<div className="relative h-48 bg-gray-700 overflow-hidden">
								{resolveImage(preset._images?.[0], API_URL) ? (
									<img
										src={resolveImage(preset._images?.[0], API_URL)}
										alt={preset.name}
										className="w-full h-full object-cover"
										onError={(e) => { e.currentTarget.src = "https://via.placeholder.com/300x200?text=No+Image"; }}
									/>
								) : (
									<div className="w-full h-full flex items-center justify-center bg-gray-700">
										<span className="text-gray-400">No Image</span>
									</div>
								)}
								<div className="absolute top-2 right-2 bg-black/70 px-2 py-1 rounded-md text-xs">
									{preset.category || "Uncategorized"}
								</div>
							</div>
							{/* Info */}
							<div className="p-4">
								<div className="flex justify-between items-start mb-2 gap-2">
									<h3 className="text-lg font-semibold truncate flex-1" title={preset.name}>{preset.name}</h3>
									<div className="flex flex-col items-end shrink-0">
										<span className="bg-cyan-600 text-white text-xs font-bold px-2 py-1 rounded">{toCurrency(preset.price)}</span>
										{preset.originalPrice && (
											<span className="text-[10px] text-gray-500 line-through decoration-red-500/50 mt-1">{toCurrency(preset.originalPrice)}</span>
										)}
									</div>
								</div>
								<p className="text-gray-400 text-sm line-clamp-2 h-10">{preset.description || "No description provided"}</p>

								<div className="flex justify-between items-center pt-3 border-t border-gray-700 mt-3">
									<div className="flex gap-2">
										<button onClick={() => openEdit(preset)} className="px-3 py-1.5 text-xs bg-gray-700 hover:bg-gray-600 rounded-md">Edit</button>
										<button onClick={() => handleDelete(preset.id)} className="px-3 py-1.5 text-xs bg-red-600 hover:bg-red-700 rounded-md">Delete</button>
									</div>
									{preset.driveLink ? (
										<a href={preset.driveLink} target="_blank" rel="noopener noreferrer" className="text-cyan-400 text-xs hover:text-cyan-300">Drive Link</a>
									) : (
										<span className="text-gray-500 text-xs">No Drive link</span>
									)}
								</div>
							</div>
						</div>
					))}
					{!filteredPresets.length && (
						<div className="col-span-full text-center py-16">
							<div className="w-24 h-24 bg-gray-800/50 rounded-full flex items-center justify-center mx-auto mb-6">
								<svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
							</div>
							<h3 className="text-xl font-semibold text-gray-300 mb-2">No Presets Found</h3>
							<p className="text-gray-500">Try changing filters or add a new preset.</p>
						</div>
					)}
				</div>
			)}

			{/* Full Page Modal */}
			{isModalOpen && (
				<div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 overflow-y-auto">
					<div className="min-h-screen bg-gray-900">
						{/* Header */}
						<div className="bg-gray-800 border-b border-gray-700 px-6 py-4">
							<div className="max-w-7xl mx-auto flex justify-between items-center">
								<div>
									<h2 className="text-3xl font-bold text-white">{editingPresetId ? "Edit Preset" : "Add New Preset"}</h2>
									<p className="text-gray-400 mt-1">Manage your preset details and settings</p>
								</div>
								<button onClick={() => { setIsModalOpen(false); resetForm(); }} className="text-gray-400 hover:text-white p-3 rounded-lg hover:bg-gray-700 transition-colors">
									<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
								</button>
							</div>
						</div>

						{/* Main Content */}
						<div className="max-w-7xl mx-auto p-6">

							<form onSubmit={handleSubmit} className="space-y-8">
								{/* Basic Information Section */}
								<div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
									<h3 className="text-xl font-semibold text-white mb-6 flex items-center">
										<svg className="w-5 h-5 mr-2 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
										</svg>
										Basic Information
									</h3>
									<div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6">
										<div className="lg:col-span-2">
											<label className="block text-sm font-medium text-gray-300 mb-3">Preset Name *</label>
											<input
												type="text"
												value={formData.name}
												onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
												className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-white placeholder-gray-400"
												placeholder="Enter preset name"
												required
											/>
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-300 mb-3">Category *</label>
											<select
												value={formData.category}
												onChange={(e) => setFormData((p) => ({ ...p, category: e.target.value }))}
												className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-white"
												required
											>
												<option value="">Select Category</option>
												{CATEGORIES.map((c) => (
													<option key={c} value={c}>{c}</option>
												))}
											</select>
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-300 mb-3">Price (₹) *</label>
											<input
												type="number"
												value={formData.price}
												onChange={(e) => setFormData((p) => ({ ...p, price: e.target.value }))}
												className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-white placeholder-gray-400"
												placeholder="Enter price"
												min="0"
												step="0.01"
												required
											/>
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-300 mb-3">Original Price (₹) (Optional)</label>
											<input
												type="number"
												value={formData.originalPrice}
												onChange={(e) => setFormData((p) => ({ ...p, originalPrice: e.target.value }))}
												className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-white placeholder-gray-400"
												placeholder="Enter original price"
												min="0"
												step="0.01"
											/>
										</div>
									</div>
								</div>

								{/* Drive Link Section */}
								<div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
									<h3 className="text-xl font-semibold text-white mb-6 flex items-center">
										<svg className="w-5 h-5 mr-2 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
										</svg>
										Google Drive Link
									</h3>
									<div className="max-w-4xl">
										<label className="block text-sm font-medium text-gray-300 mb-3">Drive Link *</label>
										<div className="flex gap-3">
											<input
												type="url"
												value={formData.driveLink}
												onChange={(e) => setFormData((p) => ({ ...p, driveLink: e.target.value }))}
												className="flex-1 px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-white placeholder-gray-400"
												placeholder="https://drive.google.com/file/d/..."
												required
											/>
											<button
												type="button"
												onClick={testDriveLink}
												disabled={isLoading.driveTest}
												className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
											>
												<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
												</svg>
												{isLoading.driveTest ? "Testing..." : "Test Link"}
											</button>
										</div>
										<p className="text-sm text-gray-400 mt-2">Paste a public Google Drive file or folder link</p>
									</div>
								</div>

								{/* Description Section */}
								<div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
									<h3 className="text-xl font-semibold text-white mb-6 flex items-center">
										<svg className="w-5 h-5 mr-2 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
										</svg>
										Description & Content
									</h3>
									<RichTextEditor
										value={formData.description}
										onChange={(value) => {
											console.log('🔍 Form - Description changed:', value);
											setFormData((p) => ({ ...p, description: value }));
										}}
										descriptionType={formData.descriptionType}
										descriptionItems={formData.descriptionItems}
										onTypeChange={(type) => {
											console.log('🔍 Form - Type changed:', type);
											setFormData((p) => ({ ...p, descriptionType: type }));
										}}
										onItemsChange={(items) => {
											console.log('🔍 Form - Items changed:', items);
											setFormData((p) => ({ ...p, descriptionItems: items }));
										}}
										placeholder="Enter description"
									/>
								</div>


								{/* Image Upload Section */}
								<div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
									<h3 className="text-xl font-semibold text-white mb-6 flex items-center">
										<svg className="w-5 h-5 mr-2 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
										</svg>
										Image Gallery
									</h3>

									{formData.imagePreviews.length > 0 && (
										<div className="mb-6">
											<div className="flex justify-between items-center mb-4">
												<p className="text-sm text-gray-300">Uploaded Images ({formData.imagePreviews.length}/30)</p>
												{formData.imagePreviews.length > 0 && (
													<button
														type="button"
														onClick={() => setFormData((p) => ({ ...p, imagePreviews: [], images: [] }))}
														className="text-sm text-red-400 hover:text-red-300 flex items-center gap-1"
													>
														<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
														</svg>
														Clear All
													</button>
												)}
											</div>
											<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4 max-h-96 overflow-y-auto p-4 bg-gray-900/50 rounded-lg border border-gray-600">
												{formData.imagePreviews.map((src, idx) => (
													<div key={`preview-${idx}-${src}`} className="relative group">
														<img src={src} alt={`Preview ${idx + 1}`} className="w-full h-24 object-cover rounded-lg border border-gray-600" />
														<button type="button" onClick={() => removeImage(idx)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
															<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
														</button>
														<div className="absolute bottom-1 right-1 bg-black bg-opacity-50 text-white text-xs px-1 rounded">
															{idx + 1}
														</div>
													</div>
												))}
											</div>
										</div>
									)}

									<div className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center hover:border-gray-500 transition-colors">
										<svg className="mx-auto h-16 w-16 text-gray-400 mb-4" stroke="currentColor" fill="none" viewBox="0 0 48 48">
											<path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 015.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
										</svg>
										<div className="space-y-2">
											<div className="flex justify-center text-sm text-gray-300">
												<label htmlFor="file-upload" className="relative cursor-pointer bg-gray-800 rounded-md font-medium text-cyan-400 hover:text-cyan-300 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-cyan-500 px-4 py-2">
													<span>Choose files</span>
													<input id="file-upload" ref={fileInputRef} name="file-upload" type="file" className="sr-only" multiple accept="image/*" onChange={handleImageChange} disabled={formData.imagePreviews.length >= 30} />
												</label>
												<p className="pl-3 self-center">or drag and drop</p>
											</div>
											<p className="text-sm text-gray-400">PNG, JPG, WEBP, RAW files up to 15MB each</p>
											{formData.imagePreviews.length > 0 && (
												<p className="text-sm text-amber-400">{30 - formData.imagePreviews.length} more images can be uploaded</p>
											)}
										</div>
									</div>
								</div>

								{/* Upload Progress */}
								{isLoading.form && uploadProgress > 0 && (
									<div className="space-y-2">
										<div className="flex justify-between text-sm text-gray-300">
											<span>Uploading images...</span>
											<span>{uploadProgress}%</span>
										</div>
										<div className="w-full bg-gray-700 rounded-full h-2">
											<div
												className="bg-cyan-600 h-2 rounded-full transition-all duration-300 ease-out"
												style={{ width: `${uploadProgress}%` }}
											></div>
										</div>
									</div>
								)}

								{/* Actions */}
								<div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
									<div className="flex justify-between items-center">
										<div className="text-sm text-gray-400">
											{editingPresetId ? "Update your preset details" : "Create a new preset with all required information"}
										</div>
										<div className="flex gap-4">
											<button
												type="button"
												onClick={() => { setIsModalOpen(false); resetForm(); }}
												className="px-6 py-3 text-base font-medium text-gray-300 hover:text-white bg-gray-800 rounded-lg border border-gray-600 hover:bg-gray-700 transition-colors"
											>
												Cancel
											</button>
											<button
												type="submit"
												disabled={isLoading.form}
												className="px-8 py-3 text-base font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 disabled:opacity-50 transition-colors flex items-center gap-2"
											>
												{isLoading.form ? (
													<>
														<svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
															<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
															<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
														</svg>
														Saving...
													</>
												) : (
													<>
														<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
														</svg>
														{editingPresetId ? "Update Preset" : "Create Preset"}
													</>
												)}
											</button>
										</div>
									</div>
								</div>
							</form>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default Presets;
